/**
 * Single source of truth for UI surface colors (light + dark).
 * Add new hardcoded colors here first, then reference `surfaceVar('key')` or
 * `var(--surface-…)` in components. Do not duplicate hex values in JSX/CSS.
 */
export const SURFACE_TOKENS = {
  /** #main-content shell behind routed pages */
  shellBg: {
    var: "--surface-shell-bg",
    light: "#ffffff",
    dark: "#111827",
  },
  /** Operations module pages (Campaigns, Riders, etc.) — not Dashboard */
  opsPageBg: {
    var: "--surface-ops-page-bg",
    light: "#ffffff",
    dark: "#111827",
  },
  /** Dashboard page only */
  dashboardPageBg: {
    var: "--surface-dashboard-page-bg",
    light: "#f8fafc",
    dark: "#111827",
  },

  /** DataTable outer panel (was --p-gray-light / #F9F9FA) */
  dataTablePanelBg: {
    var: "--surface-dt-panel-bg",
    light: "#f9f9fa",
    dark: "#1f2937",
  },
  dataTableSurfaceBg: {
    var: "--surface-dt-surface-bg",
    light: "#ffffff",
    dark: "#1f2937",
  },
  dataTableHeaderBg: {
    var: "--surface-dt-header-bg",
    light: "#f2f2f2",
    dark: "#374151",
  },
  dataTableHeaderText: {
    var: "--surface-dt-header-text",
    light: "#4b5563",
    dark: "#d1d5db",
  },
  dataTableCellText: {
    var: "--surface-dt-cell-text",
    light: "#111827",
    dark: "#f3f4f6",
  },
  dataTableBorder: {
    var: "--surface-dt-border",
    light: "#e5e7eb",
    dark: "#4b5563",
  },
  dataTableRowSelectedBg: {
    var: "--surface-dt-row-selected-bg",
    light: "#f2f5f9",
    dark: "#374151",
  },
  dataTableSelectionBarBg: {
    var: "--surface-dt-selection-bar-bg",
    light: "#ffffff",
    dark: "#1f2937",
  },
  dataTableExpandedPanelBg: {
    var: "--surface-dt-expanded-bg",
    light: "#ffffff",
    dark: "#1f2937",
  },
  dataTableExpandedHeaderBg: {
    var: "--surface-dt-expanded-header-bg",
    light: "#f9fafb",
    dark: "#374151",
  },

  /** Hero KPI summary cards (Operations list pages) */
  kpiCardBg: {
    var: "--surface-kpi-bg",
    light: "#ffffff",
    dark: "#1f2937",
  },
  kpiCardBgEnd: {
    var: "--surface-kpi-bg-end",
    light: "#f9fafb",
    dark: "#111827",
  },
  kpiCardShadow: {
    var: "--surface-kpi-shadow",
    light: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    dark: "0 0 0 1px rgb(55 65 81 / 0.45)",
  },
  kpiCardRing: {
    var: "--surface-kpi-ring",
    light: "rgb(229 231 235 / 0.8)",
    dark: "rgb(75 85 99 / 0.55)",
  },
  kpiCardTitleText: {
    var: "--surface-kpi-title-text",
    light: "#6b7280",
    dark: "#9ca3af",
  },
  kpiCardValueText: {
    var: "--surface-kpi-value-text",
    light: "#111827",
    dark: "#f9fafb",
  },
  kpiCardSubText: {
    var: "--surface-kpi-sub-text",
    light: "#4b5563",
    dark: "#9ca3af",
  },
  kpiCardHoverGlow: {
    var: "--surface-kpi-hover-glow",
    light:
      "radial-gradient(circle at 100% 0%, rgba(59, 130, 246, 0.05) 0%, transparent 50%)",
    dark: "none",
  },

  textPrimary: {
    var: "--surface-text-primary",
    light: "#111827",
    dark: "#f9fafb",
  },
  textSecondary: {
    var: "--surface-text-secondary",
    light: "#4b5563",
    dark: "#9ca3af",
  },
  textMuted: {
    var: "--surface-text-muted",
    light: "#6b7280",
    dark: "#9ca3af",
  },

  inputBg: {
    var: "--surface-input-bg",
    light: "#ffffff",
    dark: "#374151",
  },
  inputBorder: {
    var: "--surface-input-border",
    light: "#d1d5db",
    dark: "#4b5563",
  },
  filterPillBg: {
    var: "--surface-filter-pill-bg",
    light: "#f3f4f6",
    dark: "#374151",
  },
  /** Date range trigger button (reuses filter pill surface) */
  dateRangeTriggerBg: {
    var: "--surface-date-range-trigger-bg",
    light: "#f3f4f6",
    dark: "#374151",
  },
  filterPillBgDisabled: {
    var: "--surface-filter-pill-bg-disabled",
    light: "#e5e7eb",
    dark: "#4b5563",
  },
  searchFieldBg: {
    var: "--surface-search-bg",
    light: "rgba(0, 0, 0, 0.04)",
    dark: "rgba(55, 65, 81, 0.55)",
  },
  searchFieldBorder: {
    var: "--surface-search-border",
    light: "#e5e7eb",
    dark: "#4b5563",
  },

  paginationBtnBg: {
    var: "--surface-pagination-btn-bg",
    light: "#ffffff",
    dark: "#374151",
  },
  paginationBtnBorder: {
    var: "--surface-pagination-btn-border",
    light: "#e5e7eb",
    dark: "#4b5563",
  },
  paginationBtnText: {
    var: "--surface-pagination-btn-text",
    light: "#111827",
    dark: "#e5e7eb",
  },

  modalBg: {
    var: "--surface-modal-bg",
    light: "#ffffff",
    dark: "#1f2937",
  },
  dropdownMenuBg: {
    var: "--surface-dropdown-menu-bg",
    light: "#ffffff",
    dark: "#374151",
  },
  dropdownMenuBorder: {
    var: "--surface-dropdown-menu-border",
    light: "#d1d5db",
    dark: "#4b5563",
  },

  tooltipBg: {
    var: "--surface-tooltip-bg",
    light: "#ffffff",
    dark: "#374151",
  },
  tooltipText: {
    var: "--surface-tooltip-text",
    light: "#1f2937",
    dark: "#f3f4f6",
  },
  tooltipBorder: {
    var: "--surface-tooltip-border",
    light: "#d1d5db",
    dark: "#4b5563",
  },

  /** Sidebar right edge + logo divider */
  sidebarBorder: {
    var: "--sidebar-border",
    light: "#e5e7eb",
    dark: "#4b5563",
  },
};

/** CSS `var(--surface-…)` reference for a token key */
export function surfaceVar(tokenKey) {
  const token = SURFACE_TOKENS[tokenKey];
  if (!token) {
    console.warn(`[surfaceTokens] Unknown token: ${tokenKey}`);
    return "inherit";
  }
  return `var(${token.var})`;
}

/** Resolved color value for the active theme (for inline styles / react-select) */
export function surfaceColor(tokenKey, theme = "light") {
  const token = SURFACE_TOKENS[tokenKey];
  if (!token) return undefined;
  return theme === "dark" ? token.dark : token.light;
}

export function getSurfaceCssVariables(theme = "light") {
  const isDark = theme === "dark";
  const vars = {};

  Object.values(SURFACE_TOKENS).forEach((token) => {
    vars[token.var] = isDark ? token.dark : token.light;
  });

  // Legacy aliases used across the app — keep in sync from tokens
  vars["--p-gray-light"] = vars["--surface-dt-panel-bg"];
  vars["--input-background-gray"] = vars["--surface-input-bg"];
  vars["--surface-date-range-trigger-bg"] = vars["--surface-filter-pill-bg"];
  vars["--sidebar-border"] = vars["--surface-dt-border"];

  return vars;
}
